import React from "react";
import axios from "axios";
import Hls from "hls.js";
import { getQueryVariable } from "Helpers/QueryString"

import VideoControls from "./VideoControls"
import CheckBandwidth from "./CheckBandwidth"
import { languages, languageKeys } from "../Helpers/languages";
import { BASE_PATH } from "../constants/awsBaseUrl";

import "./NewFuncComponents/Video.css"

const checkCaptions = async (video, translations=[]) => {
  let toReturn = []
  try {
    let res = await axios.head(`${BASE_PATH}/captions/${video}.vtt`)
    toReturn.push({
      key: "0",
      src: `${BASE_PATH}/captions/${video}.vtt`,
      srcLang: "en",
      label: "English",
      kind: "captions"
    })
  } catch (err) {
    console.error("Error heading caption file:")
    console.error(err)
  }
  for(let lang of translations) {
    try {
      let res = await axios.head(`${BASE_PATH}/captions-translated/${video}/${video}-${lang}.vtt`)
      toReturn.push({
        key: lang,
        label: `${languages.find((lan) => lan.key === lang)?.language}`,
        kind: "captions",
        srcLang: lang,
        src: `${BASE_PATH}/captions-translated/${video}/${video}-${lang}.vtt`
      })
    } catch (err) {
      console.error("Error heading translated caption file:")
      console.error(err)
    }
  }
  return toReturn
}

class Video extends React.Component {

  constructor(props) {
    super(props)
    this.videoplayer = React.createRef();
    this.audioplayer = React.createRef();
    // We don't want react to update when this changes, so that is why it is not in state
    this.bufferSize = 0
    // Ditto for this
    this.maxLoadedFrag = -1
  }

  state = {
    updating: false,
    user: this.props.pub_user ? this.props.pub_user._id : "", // this is the viewer in the DB
    customThumb:false,
    captions: [],
    currentCaptions: "",
    captionsChanged: new Date(),
    secondaryAudio: -1,
    // For video controls
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    muted: false,
    displayControlsFS: false,
  };

  componentDidMount() {
    if (this.props?.downloadsDisabled) {
      this.disableContextMenu();
    };
    if(this.props.secondaryAudioTracks.length) {
      this.avInterval = setInterval(this.syncAudioVideo, 250)
    }

    // check if the video is a live stream then fetch the live stream from aws
    if(this.props.videoURL) {
      if(this.props.type !== "live") {
        checkCaptions(this.state.file || this.props.video, this.props.translations)
          .then((res) => {this.setState({captions: res})})
          .catch((err) => {console.error("Error checking for caption files:");console.error(err)})
      }
      if(this.props.format === "HLS") {
        this.createhls(this.props.videoURL)
      } else if(this.props.format === "MP4") {
        this.createmp4(this.props.videoURL)
      }
    }
    if(this.videoplayer.current) {
      this.videoplayer.current.textTracks.addEventListener("change", this.onChangeCaptions)
    }
    // this.loadCaptionsData();
    this.mounted = true;
    this.start();
  }

  onChangeCaptions = (e) => {
    if(this.state.currentCaptions) {
      // For some reason switching from subtitles to captions causes all text tracks to turn off.
      for(let track of e.currentTarget) {
        if(`${track.kind}/${track.language}/${track.label}` === this.state.currentCaptions) {
          if(track.mode !== "showing") {
            track.mode = "showing"
          }
          break
        }
      }
    }
    this.setState({captionsChanged: new Date()})
  }

  start = () => {
    if (this.mounted) {
      setTimeout(() => {
        this.start();
      }, 1000);
    }
    try {
      this.props.update(document.getElementById("video").currentTime);
    } catch (e) {
      this.mounted = false;
    }
  };

  // The names of the translations can sometimes be keys. That does not
  // look good to the user therefore.
  assignKeyCaptions = (languageKey='es') => {
    try {
      if (!(languageKey in languageKeys)) {
        throw new Error('Language Key DNE in languages list!');
      };
      return languages[languageKey];
    } catch (err) {
      console.error('assignKeyCaptions - ', err);
      return languageKey;
    };
  }

  // used for getting rid of the dropdown menu for right clicking
  disableContextMenu = () => {
    this.videoplayer.current.addEventListener('contextmenu', this.preventDefault);
  }

  // remove listener when user cursor moves awat from video
  removeContextMenuListener = () => {
    this.videoplayer.current.removeEventListener('contextmenu', this.preventDefault);
  }

  // this is a helper to make sure default doesnt prevent functionality
  preventDefault = (event) => {
    event.preventDefault();
  };

  componentWillUnmount() {
    clearInterval(this.avInterval)
    if (this.props?.downloadsDisabled) {
      this.removeContextMenuListener();
    };
    if(this.videoplayer.current) {
      this.videoplayer.current.textTracks.removeEventListener("change", this.onChangeCaptions)
    }
    if(this.hideFSTimeout) {
      clearTimeout(this.hideFSTimeout)
    }
    this.setViews(true)
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    if (!this.state.updating && this.props.shouldUpdate) {
      this.setState({ updating: true });
      document.getElementById("video").currentTime = this.props.scrubTo;
      this.props.clearUpdate();
      this.setState({ updating: false });
    }
    if (prevProps.videoURL !== this.props.videoURL) {
      if(this.hls) {
        this.hls.destroy()
      }
      if(this.props.format === "HLS") {
        this.createhls(this.props.videoURL)
      } else if(this.props.format === "MP4") {
        this.createmp4(this.props.videoURL)
      }
    }
    if(!!this.props.secondaryAudioTracks.length !== !!prevProps.secondaryAudioTracks.length) {
      if(this.props.secondaryAudioTracks.length) {
        this.avInterval = setInterval(this.syncAudioVideo, 250)
      } else {
        clearInterval(this.avInterval)
      }
    }
    if (this.props.file.metadata) {
      this.insAndOuts(this.props.file.metadata);
    }
  }


  createmp4 = (url) => {
    let video = this.videoplayer.current;
    video.src = url;
    document.getElementById("video").currentTime = getQueryVariable(
      "start"
    );
    let playPromise = video.play();
    // Handling rejected promise from video.play to prevent the DOMException that comes from an unhandled play promise
    playPromise.catch((err) => {
      if(err.name === "AbortError") {
        // This is expected if playback was aborted because the video player unmounted in between play being called and resolving,
        // so no need to log in that case.
        if(this.mounted) {
          console.error(`Video Playback aborted: ${err.message}`)
        }
      } else {
        console.error(`Error playing video: ${err}`)
      }
    })
  };

  createhls = (url) => {
    if (Hls.isSupported()) {
      let video = this.videoplayer.current;
      this.bufferSize = 0;
      let hls = new Hls({ 
        autoStartLoad: true, 
        enableCEA708Captions: true,
        manifestLoadingTimeOut: 20000,
        manifestLoadingMaxRetry: 3,
        manifestLoadingRetryDelay: 1000
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(url);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        document.getElementById("video").currentTime = getQueryVariable(
          "start"
        );
        let playPromise = video.play();
        // Handling rejected promise from video.play to prevent the DOMException that comes from an unhandled play promise
        playPromise.catch((err) => {
          if(err.name === "AbortError") {
            // This is expected if playback was aborted because the video player unmounted in between play being called and resolving,
            // so no need to log in that case.
            if(this.mounted) {
              console.error(`HLS Playback aborted: ${err.message}`)
            }
          } else {
            console.error(`Error playing hls: ${err}`)
          }
        })
      });
      hls.on(Hls.Events.BUFFER_APPENDING, (e, data) => {
        if(data && data.data) {
          this.bufferSize += data.data.byteLength
        }
      })
      // If the m3u8 goes back to fragment 0, that means that the ip output was probably restarted
      // When this happens, we need to restart Hls.js in order to continue playback correctly.
      hls.on(Hls.Events.FRAG_LOADING, (event, data) => {
        if(this.props.type === "live") {
          let {frag} = data
          if(frag.sn > this.maxLoadedFrag) {
            this.maxLoadedFrag = frag.sn
          } else if(frag.sn === 0) {
            // RESET
            this.maxLoadedFrag = -1
            hls.destroy();
            this.createhls(this.props.videoURL);
          }
        }
      })
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('fatal media error encountered, try to recover');
              hls.recoverMediaError();
              break;
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('fatal network error encountered', data);
              // All retries and media options have been exhausted.
              // Immediately trying to restart loading could cause loop loading.
              // Consider modifying loading policies to best fit your asset and network
              // conditions (manifestLoadPolicy, playlistLoadPolicy, fragLoadPolicy).
              break;
            default:
              // cannot recover
              hls.destroy();
              // recreate if live
              if(this.props.type === "live") {
                this.createhls(this.props.videoURL);
              }
              break;
          }
        }
      });
      this.hls = hls;
    }

  };

  showControls = () => {
    this.setState({displayControlsFS: true})
    if(this.hideFSTimeout) {
      clearTimeout(this.hideFSTimeout)
    }
    this.hideFSTimeout = setTimeout(() => {
      if(this.mounted) {
        this.setState({displayControlsFS: false})
      }
    }, 3000)
  }

  getCaptions = (newCaptions=null) => {
    if (this.props.type === "live") {
      return null;
    } else if(this.state.captions && this.state.captions.length) {
      let captions = this.state.captions
      if (newCaptions !== null) {
        this.setState({ ...this.state, captions: newCaptions });
        captions = newCaptions
      };
      // Fix for Shoreline:
      // If we know for sure we have english captions, then filter out any "undefined" language tracks (they are English as well).
      let hasEnglish = captions.find((track) => (track.srcLang === "en" || track.srcLang === "en-US" || track.label.toLowerCase() === "english"))
      return captions
      .filter((track) => (
        !hasEnglish || (track.srcLang !== "un" && track.label.toLowerCase() !== "undefined")
      ))
      .map((track) => {
        if(!hasEnglish && (track.srcLang === "un" || track.label.toLowerCase() === "undefined")) {
          track = {
            ...track,
            srcLang: "en",
            label: "English"
          }
        }
        return (<track {...track} id={"cap" + track.key}/>)
      })
    }
  };

  setViews = (force=false) => {
    let viewData = {}
    if(this.videoplayer.current) {
      if(this.videoplayer.current.videoWidth && this.videoplayer.current.videoHeight) {
        viewData.vWidth = this.videoplayer.current.videoWidth
        viewData.vHeight = this.videoplayer.current.videoHeight
      }
      if(this.videoplayer.current.played) {
        let ranges = this.videoplayer.current.played
        let arr = []
        for(let i = 0; i < ranges.length; i++) {
          arr.push({
            start: ranges.start(i),
            end: ranges.end(i)
          })
        }
        if(arr.length) {
          viewData.played = arr
        }
      }
    }
    this.props.setViews(force, viewData, this.props.type)
  }

  insAndOuts = (metadata) => {
    let ins = parseInt(metadata.in ? metadata.in : metadata.In, 10);
    let outs = parseInt(metadata.out ? metadata.out : metadata.Out, 10);
    var time = document.getElementById('video');

    if (time.currentTime < ins) {
      time.currentTime = ins;
    } else if (time.currentTime > outs) {
      time.currentTime = outs;
      if(this.mounted && this.videoplayer.current) {
        this.videoplayer.current.pause()
      }
    }
  }

  filterValidCaptions = () => {
    try {
      const videoElement = this.videoplayer?.current;
      if (videoElement && videoElement?.textTracks && videoElement?.textTracks?.length > 0) {
        const newValues = Array.from(videoElement.textTracks).map((track) => {
          if (track?.label && track?.label?.length <= 2) {
            track.label = this.assignKeyCaptions(track?.language);
          };
          return track;
        });
        console.log('new styuff', newValues);
        this.getCaptions(newValues);
        return newValues;
      };
      return videoElement?.textTracks;
    } catch (err) {
      console.error(err);
    };
  }

  syncVideoAudio = () => {
    const driftThreshold = 0.08; // seconds
    if (!this.videoplayer.current || !this.audioplayer.current) return;
    if (this.videoplayer.current.paused || this.audioplayer.current.paused) return;
    const drift = this.audioplayer.current.currentTime - this.videoplayer.current.currentTime;
    if (Math.abs(drift) > driftThreshold) {
      this.audioplayer.current.currentTime = Math.min(this.videoplayer.current.currentTime, this.audioplayer.current.duration);
    }
  }

  togglePlaying = () => {
    if(this.videoplayer.current) {
      if(this.videoplayer.current.paused || this.videoplayer.current.ended) {
        this.videoplayer.current.play()
      } else {
        this.videoplayer.current.pause()
      }
    }
  }

  setCurrentCaptions = (toSet) => {
    if(this.videoplayer.current) {
      for(let track of this.videoplayer.current.textTracks) {
        if(track === toSet) {
          track.mode = "showing"
        } else {
          track.mode = "disabled"
        }
      }
      if(toSet) {
        this.setState({currentCaptions: `${toSet.kind}/${toSet.language}/${toSet.label}`})
      } else {
        this.setState({currentCaptions: ""})
      }
    }
  }

  setCurrentSAP = (index) => {
    this.setState({secondaryAudio: index})
    if(this.props.format === "HLS" && this.hls) {
      if(index === -1) {
        this.hls.setAudioOption({default: true})
      } else {
        let track = this.props.secondaryAudioTracks[index]
        this.hls.setAudioOption({name: track.label, id: track.trackNumber - 1})
      }
    }
    if(this.audioplayer.current) {
      this.audioplayer.current.muted = index < 0
      this.audioplayer.current.currentTime = Math.min(this.videoplayer.current.currentTime, this.audioplayer.current.duration);
    }
  }

  render() {
    let audioElement;
    if(this.props.secondaryAudioTracks.length && this.props.secondaryAudioTracks[0].url && !(this.props.format === "HLS" && this.props.secondaryAudioTracks[0].trackNumber)) {
      audioElement = <audio autoPlay
        ref={this.audioplayer}
        muted={true}>
        <source src={this.props.secondaryAudioTracks[0].url}/>
      </audio>
    }
    let containerClass = ""
    if(document.fullscreenElement && !this.state.displayControlsFS) {
      containerClass = "no-pointer"
    }
    return (
      <figure id="video-player-container" className={containerClass} onMouseMove={(e) => {if(document.fullscreenElement === e.currentTarget) this.showControls()}}>
        <video
          width="100%"
          ref={this.videoplayer}
          id="video"
          className={this.props.embed?"embeddedVideo":""}
          crossOrigin="anonymous"
          poster={this.props.thumbnailURL}
          onClick={(e) => {
            if(e.currentTarget === this.videoplayer.current) {
              this.togglePlaying()
            }
          }}
          onLoadedMetadata={(e) => {
            this.setState({duration: e.currentTarget.duration, volume: e.currentTarget.volume, muted: e.currentTarget.muted})
          }}
          onTimeUpdate={(e) => {
            this.setState({currentTime: e.currentTarget.currentTime})
            this.setViews()
          }}
          onPause={() => {
            this.setState({playing: false})
            this.setViews(true)
            if(this.audioplayer.current) {
              this.audioplayer.current.pause()
            }
          }}
          onPlay={(e) => {
            this.setState({playing: true})
            this.setViews(true)
            if(this.audioplayer.current && e.currentTarget && e.currentTarget.currentTime < this.audioplayer.current.duration) {
              this.audioplayer.current.currentTime = e.currentTarget.currentTime
              this.audioplayer.current.play()
            }
            if(this.hls) {
              console.log(this.hls.allAudioTracks)
              console.log(this.hls.audioTracks)
            }
          }}
          onVolumeChange={(e) => {
            this.setState({volume: e.currentTarget.volume, muted: e.currentTarget.muted})
            if(this.audioplayer.current) {
              this.audioplayer.current.muted = e.currentTarget.muted
              this.audioplayer.current.volume = e.currentTarget.volume
            }
          }}
          onSeeked={(e) => {
            if(this.audioplayer.current) {
              this.audioplayer.current.currentTime = e.currentTarget.currentTime
            }
          }}
          style={{ borderRadius: '4px' }}
          onLoad={() => this.filterValidCaptions()}
          playsInline
          // onLoadedMetadata={() => this.filterValidCaptions()}
        >
          <source src={this.props.videoURL} type="application/x-mpegURL" />
          {this.getCaptions()}
        </video>
        {audioElement}
        <VideoControls
          playing={this.state.playing}
          currentTime={this.state.currentTime}
          duration={this.state.duration}
          volume={this.state.volume}
          muted={this.state.muted}
          captions={this.videoplayer.current?.textTracks}
          captionsChanged={this.state.captionsChanged}
          secondaryAudioTracks={this.props.secondaryAudioTracks}
          currentSAP={this.state.secondaryAudio}
          isLive={this.props.type === "live"}
          shouldDisplayFullscreen={this.state.displayControlsFS}
          togglePlaying={this.togglePlaying}
          toggleMuted={() => {
            if(this.videoplayer.current) {
              this.videoplayer.current.muted = !this.videoplayer.current.muted
              if(!this.videoplayer.current.muted && this.videoplayer.current.volume === 0) {
                this.videoplayer.current.volume = 1
              }
              if(this.audioplayer.current) {
                this.audioplayer.current.muted = this.state.secondaryAudio > -1 ? this.videoplayer.current.muted : true
              }
            }
          }}
          setVolume={(vol) => {
            if(this.videoplayer.current) {
              this.videoplayer.current.volume = vol
              if(this.videoplayer.current.volume === 0 && !this.videoplayer.current.muted) {
                this.videoplayer.current.muted = true
              } else if(vol > 0 && this.videoplayer.current.muted) {
                this.videoplayer.current.muted = false
              }
            }
          }}
          seek={(newTime) => {
            this.setState({currentTime: newTime})
            if(this.videoplayer.current) {
              this.videoplayer.current.currentTime = newTime
            }
          }}
          toggleFullscreen={() => {
            if (document.fullscreenElement !== null) {
              // The document is in fullscreen mode
              document.exitFullscreen();
            } else {
              // The document is not in fullscreen mode
              document.getElementById("video-player-container").requestFullscreen();
              this.showControls()
            }
          }}
          setCaptions={this.setCurrentCaptions}
          setCurrentSAP={this.setCurrentSAP}
        />
      </figure>
    );
  }
}
export default CheckBandwidth(Video);
